"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/casting/Vacuum-Casting/Service";

const ITEMS = [
	{ src: `${IMAGE_BASE}/vacuum-casting-service-1.webp` },
	{ src: `${IMAGE_BASE}/vacuum-casting-service-2.webp` },
	{ src: `${IMAGE_BASE}/vacuum-casting-service-3.webp` },
	{ src: `${IMAGE_BASE}/vacuum-casting-service-4.webp` },
	{ src: `${IMAGE_BASE}/vacuum-casting-service-5.webp` },
	{ src: `${IMAGE_BASE}/vacuum-casting-service-6.webp` },
] as const;

export default function UrethaneCastingService() {
	const t = useTranslations("new-vacuum-casting");

	const containerRef = useRef<HTMLDivElement>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isMobile, setIsMobile] = useState(false);
	const [containerWidth, setContainerWidth] = useState(0);

	useEffect(() => {
		const update = () => {
			setIsMobile(window.innerWidth < 768);
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	const visibleCount = isMobile ? 2 : 4;
	const gap = 16;
	const itemWidth = containerWidth > 0 ? (containerWidth - (visibleCount - 1) * gap) / visibleCount : 0;
	const maxIndex = Math.max(0, ITEMS.length - visibleCount);
	const slideCount = maxIndex + 1;

	useEffect(() => {
		if (currentIndex > maxIndex) setCurrentIndex(0);
	}, [maxIndex, currentIndex]);

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
	}, [maxIndex]);

	useEffect(() => {
		if (slideCount <= 1) return;
		const timer = setInterval(nextSlide, 4000);
		return () => clearInterval(timer);
	}, [nextSlide, slideCount]);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("urethaneServiceTitle")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-black sm:mt-4 md:text-base">
						{t("urethaneServiceDesc")}
					</p>
				</header>

				<div ref={containerRef} className="relative overflow-hidden">
					<div
						className="flex gap-4 transition-transform duration-500 ease-in-out"
						style={{
							transform: `translateX(-${currentIndex * (itemWidth + gap)}px)`,
						}}
					>
						{ITEMS.map((item, index) => (
							<div
								key={item.src}
								className="flex-shrink-0"
								style={{
									width: `calc((100% - ${visibleCount - 1}rem) / ${visibleCount})`,
								}}
							>
								<div className="relative aspect-[4/3] w-full overflow-hidden bg-[#bfbfbf]">
									<Image
										src={item.src}
										alt={`${t("urethaneServiceImageAlt")} ${index + 1}`}
										fill
										className="object-cover"
										sizes="(max-width: 768px) 50vw, 25vw"
									/>
								</div>
							</div>
						))}
					</div>
				</div>

				{slideCount > 1 && (
					<div className="mt-6 flex items-center justify-center gap-2 md:mt-8">
						{Array.from({ length: slideCount }, (_, i) => (
							<button
								key={i}
								type="button"
								onClick={() => setCurrentIndex(i)}
								className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
									currentIndex === i ? "bg-[#9acd32]" : "bg-[#c8e69c]"
								}`}
								aria-label={`Go to page ${i + 1}`}
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
